<template>
  <section class="hero-section">
    <!-- Optimized background image for better LCP -->
    <img
      src="/images/billiards-4-1920w.webp"
      alt="Professional pool table background"
      class="hero-bg-image"
      loading="eager"
      fetchpriority="high"
    />
    <div class="hero-background">
      <div class="pattern-overlay"></div>
      <div class="gradient-overlay"></div>
    </div>
    
    <div class="hero-container">
      <div class="hero-content">
        <!-- Main Hero Message -->
        <div class="hero-main">
          <div class="hero-badge">
            <Icon name="mdi:trophy-variant" />
            <span>Premier Pool League</span>
          </div>
          
          <h1 class="hero-title">
            <span class="title-primary">Experience Championship</span>
            <span class="title-accent">Pool Competition</span>
            <span class="title-secondary">in the Heart of Texas</span>
          </h1>
          
          <p class="hero-description">
            Join The Players League DFW, where skilled players compete in the prestigious 
            <strong>Bad Boys Tru-Team Format</strong>. Two premier locations, weekly matches, 
            and a community dedicated to the art of competitive pool.
          </p>
          
          <div class="hero-actions">
            <NuxtLink to="/about" class="hero-btn primary">
              <Icon name="mdi:information-outline" aria-hidden="true" />
              <span>Learn About Our League</span>
            </NuxtLink>
            <NuxtLink to="/contact" class="hero-btn secondary">
              <Icon name="mdi:account-plus-outline" />
              <span>Join Today</span>
            </NuxtLink>
          </div>
        </div>
      </div>
      
      <!-- MVP Standings Cards -->
      <MVPStandingsCards />
      
      <!-- Hero Features -->
      <div class="hero-features">
        <div class="feature-highlight">
          <h2>Why Choose Our League?</h2>
          <div class="features-grid">
            <div class="feature-item">
              <Icon name="mdi:shield-check" aria-hidden="true" />
              <div>
                <h3>Professional Standards</h3>
                <p>Official Bad Boys Tru-Team Format ensuring fair and competitive play</p>
              </div>
            </div>
            <div class="feature-item">
              <Icon name="mdi:map-marker" aria-hidden="true" />
              <div>
                <h3>Convenient Locations</h3>
                <p>Two premier locations across the Dallas-Fort Worth metroplex</p>
              </div>
            </div>
            <div class="feature-item">
              <Icon name="mdi:calendar-check" aria-hidden="true" />
              <div>
                <h3>Regular Schedule</h3>
                <p>Consistent weekly matches with flexible timing options</p>
              </div>
            </div>
            <div class="feature-item">
              <Icon name="mdi:account-heart" aria-hidden="true" />
              <div>
                <h3>Welcoming Community</h3>
                <p>Inclusive environment for players of all skill levels</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
</script>

<style scoped>
.hero-section {
  position: relative;
  min-height: 90vh;
  display: flex;
  align-items: center;
  overflow: hidden;
}

/* Optimized background image */
.hero-bg-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

.hero-background {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(30, 58, 138, 0.8) 0%, rgba(15, 23, 42, 0.9) 30%, rgba(29, 78, 216, 0.8) 100%);
  z-index: 1;
}

.pattern-overlay {
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 20% 20%, rgba(255, 184, 28, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(255, 184, 28, 0.08) 0%, transparent 50%),
    repeating-linear-gradient(
      45deg,
      transparent,
      transparent 20px,
      rgba(255, 184, 28, 0.02) 20px,
      rgba(255, 184, 28, 0.02) 40px
    );
}

.gradient-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.1) 100%);
}

.hero-container {
  position: relative;
  z-index: 2;
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 24px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 48px;
}

/* Main Hero Content */
.hero-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 48px;
  align-items: start;
}

.hero-main {
  max-width: 600px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 184, 28, 0.15);
  border: 1px solid var(--gold);
  color: var(--gold);
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 32px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 16px rgba(255, 184, 28, 0.2);
}

.hero-title {
  font-family: var(--font-heading);
  margin-bottom: 32px;
  line-height: 1.1;
}

.title-primary {
  display: block;
  font-size: 2.2rem;
  color: var(--cream);
  font-weight: 300;
  opacity: 0.95;
  margin-bottom: 8px;
}

.title-accent {
  display: block;
  font-size: 4rem;
  font-weight: 800;
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 50%, var(--gold) 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  margin-bottom: 8px;
}

.title-secondary {
  display: block;
  font-size: 1.4rem;
  color: var(--cream);
  font-weight: 400;
  opacity: 0.85;
}

.hero-description {
  font-size: 1.2rem;
  line-height: 1.7;
  color: var(--cream);
  margin-bottom: 40px;
  opacity: 0.9;
}

.hero-description strong {
  color: var(--gold);
  font-weight: 600;
}

.hero-actions {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  margin-left: 5px;
}

.hero-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 32px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.hero-btn.primary {
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  color: var(--navy-blue);
  box-shadow: 0 6px 20px rgba(255, 184, 28, 0.3);
}

.hero-btn.primary:hover {
  background: linear-gradient(135deg, var(--light-gold) 0%, var(--gold) 100%);
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(255, 184, 28, 0.4);
}

.hero-btn.secondary {
  background: transparent;
  color: var(--gold);
  border: 2px solid var(--gold);
  box-shadow: 0 4px 16px rgba(255, 184, 28, 0.2);
}

.hero-btn.secondary:hover {
  background: var(--gold);
  color: var(--navy-blue);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(255, 184, 28, 0.3);
}

/* Hero Features */
.hero-features {
  margin-top: 48px;
}

.feature-highlight {
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 40px;
  border: 1px solid rgba(255, 184, 28, 0.2);
}

.feature-highlight h2 {
  color: var(--gold);
  font-size: 1.8rem;
  font-family: var(--font-heading);
  text-align: center;
  margin-bottom: 32px;
  position: relative;
}

.feature-highlight h2::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 2px;
  background: var(--gold);
  border-radius: 1px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.feature-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 184, 28, 0.1);
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 184, 28, 0.3);
  transform: translateY(-2px);
}

.feature-item > svg {
  color: var(--gold);
  font-size: 1.5rem;
  margin-top: 4px;
  flex-shrink: 0;
}

.feature-item h3 {
  color: var(--gold);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
  font-family: var(--font-heading);
}

.feature-item p {
  color: var(--cream);
  font-size: 0.9rem;
  line-height: 1.5;
  opacity: 0.9;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .hero-content {
    grid-template-columns: 1fr;
    gap: 32px;
    text-align: center;
  }
  
  .hero-main {
    max-width: none;
  }
  
  .title-accent {
    font-size: 3.5rem;
  }
}

@media (max-width: 768px) {
  .hero-section {
    min-height: 70vh; /* Reduced from 90vh for better content preview */
  }
  
  .hero-container {
    padding: 32px 16px;
    gap: 32px;
  }
  
  .title-primary {
    font-size: 1.8rem;
  }
  
  .title-accent {
    font-size: 2.8rem;
  }
  
  .title-secondary {
    font-size: 1.2rem;
  }
  
  .hero-description {
    font-size: 1.1rem;
  }
  
  .hero-actions {
    justify-content: center;
    flex-direction: column;
  }
  
  .hero-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
  
  .feature-highlight {
    padding: 32px 24px;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .hero-section {
    min-height: 60vh; /* Even more compact on small phones */
  }
  
  .hero-container {
    padding: 24px 12px;
  }
  
  .title-accent {
    font-size: 2.2rem;
  }
  
  .hero-description {
    font-size: 1rem;
  }
  
  .feature-item {
    padding: 16px;
  }
}

/* Extra small devices optimization */
@media (max-width: 360px) {
  .hero-section {
    min-height: 55vh;
  }
  
  .title-accent {
    font-size: 1.9rem;
    line-height: 1.1;
  }
  
  .hero-description {
    font-size: 0.95rem;
  }
  
  .hero-badge {
    padding: 8px 16px;
    font-size: 0.8rem;
  }
}
</style>
